import type {
  StripeInvoicesList,
  StripeProduct,
  StripeSubscription,
} from "@/API";
import Text from "@/components/Text";
import { useEffect, type Dispatch, type FC, type SetStateAction } from "react";
import CurrentSubCard from "../CurrentSubCard";
import InvoiceRow from "@/components/InvoiceRow";
import { Button } from "@/components/ui/button";
import type { View } from "../..";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import {
  createCheckoutSession,
  createCheckoutSessionState,
} from "@/store/checkoutSessionState";

type Props = {
  stripeSubscription: StripeSubscription | null;
  listProducts: StripeProduct[] | null;
  listInvoices: StripeInvoicesList | null;
  setView: Dispatch<SetStateAction<View>>;
  stripeSubscriptionIsLoading: boolean;
  listProductsIsLoading: boolean;
};

const General: FC<Props> = ({
  stripeSubscription,
  listInvoices,
  listProducts,
  setView,
  stripeSubscriptionIsLoading,
  listProductsIsLoading,
}) => {
  const { t } = useTranslation();
  const { data: userPlan } = currentSubscriptionState();
  const { data, isLoading } = createCheckoutSessionState();

  const product =
    listProducts &&
    stripeSubscription &&
    listProducts.find(({ prices }) =>
      prices.find((price) => price?.id === stripeSubscription.priceId),
    );

  useEffect(() => {
    if (data && data.clientSecret) {
      window.location.href = data.clientSecret;
    }
  }, [data]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
      <div className="flex flex-col gap-[16px]">
        <Text fontWeight={600} fontSize={20}>
          {t("current_plan")}
        </Text>

        <CurrentSubCard
          isLoading={stripeSubscriptionIsLoading || listProductsIsLoading}
          setView={setView}
          productName={product?.name ?? undefined}
          expirationDate={
            userPlan?.cancelAtPeriodEnd ? userPlan.currentPeriodEnd : undefined
          }
        />
      </div>
      <div className="flex flex-col gap-[24px]">
        {stripeSubscription && (
          <div className="flex flex-col gap-[16px]">
            <Text fontWeight={600} fontSize={20}>
              {t("payments")}
            </Text>
            <div className="flex flex-col gap-[16px] rounded-[12px] bg-[#FFFFFF] p-[16px]">
              <div className="flex flex-col gap-[4px]">
                <Text fontWeight={600} fontSize={16}>
                  {t("last_payment")}
                </Text>
                <Text fontWeight={400} fontSize={14} className="capitalize">
                  {dayjs(stripeSubscription.currentPeriodStart).format(
                    "dddd D MMMM YYYY",
                  )}
                </Text>
              </div>
              {!stripeSubscription.cancelAtPeriodEnd && (
                <>
                  <hr className="w-full" />
                  <div className="flex flex-col gap-[4px]">
                    <Text fontWeight={600} fontSize={16}>
                      {t("next_payment")}
                    </Text>
                    <Text fontWeight={400} fontSize={14} className="capitalize">
                      {dayjs(stripeSubscription.currentPeriodEnd).format(
                        "dddd D MMMM YYYY",
                      )}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-[16px]">
          <Text fontWeight={600} fontSize={20}>
            {t("billing")}
          </Text>
          {listInvoices &&
            listInvoices.invoices.slice(0, 3).map((invoice) => {
              if (!invoice) return null;

              return (
                <InvoiceRow invoice={invoice} key={`invoice-${invoice.id}`} />
              );
            })}
          <Button
            onClick={() => setView("invoices")}
            variant="secondaryOutlined"
            className="w-[min-content]"
          >
            <Text fontWeight={600} fontSize={14}>
              {t("manage_bills")}
            </Text>
          </Button>
          <Button
            disabled={isLoading}
            onClick={() =>
              createCheckoutSession({ priceId: "N/A", isSetup: true })
            }
            className="mt-[24px] sm:w-[fit-content]"
          >
            {t("add_payment_method")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default General;
